"use client";

import { useEffect, useMemo, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import AutoScroll from "embla-carousel-auto-scroll";

import { useGetFeedItemsQuery, useGetDailyTotalsQuery } from "@/app/redux/features/api/winnersFeed";
import FeedCardVertical from "@/app/(general-header)/home/winnersFeed/FeedCardVertical";
import RotatingStats from "@/app/(general-header)/home/winnersFeed/RotatingStats";

const POLLING_INTERVAL = 120_000;
const MIN_SLIDES_FOR_MARQUEE = 8;

export default function WinnersFeedGrid({ stopped = false }: { stopped?: boolean }) {
  const { data: feedItems, isLoading: isFeedLoading } = useGetFeedItemsQuery(undefined, {
    pollingInterval: POLLING_INTERVAL,
  });
  const { data: dailyTotals } = useGetDailyTotalsQuery(undefined, {
    pollingInterval: POLLING_INTERVAL,
  });

  const items = useMemo(() => feedItems ?? [], [feedItems]);

  const slides = useMemo(() => {
    if (items.length === 0 || items.length >= MIN_SLIDES_FOR_MARQUEE) return items;
    const repeats = Math.ceil(MIN_SLIDES_FOR_MARQUEE / items.length);
    return Array.from({ length: repeats }, () => items).flat();
  }, [items]);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, dragFree: true, align: "start" }, [
    AutoScroll({ speed: 1.2, startDelay: 0, stopOnInteraction: true, stopOnMouseEnter: false }),
  ]);

  const isDraggingRef = useRef(false);
  const isMouseOverRef = useRef(false);
  const manuallyStoppedRef = useRef(false);

  useEffect(() => {
    if (!emblaApi) return;

    const autoScroll = emblaApi.plugins().autoScroll;
    if (!manuallyStoppedRef.current) autoScroll?.play();

    const rootNode = emblaApi.rootNode();

    const onPointerDown = () => {
      isDraggingRef.current = true;
    };
    const onSettle = () => {
      isDraggingRef.current = false;
      if (!isMouseOverRef.current && !manuallyStoppedRef.current) autoScroll?.play();
    };
    const onMouseEnter = () => {
      isMouseOverRef.current = true;
      if (!isDraggingRef.current) autoScroll?.stop();
    };
    const onMouseLeave = () => {
      isMouseOverRef.current = false;
      if (!isDraggingRef.current && !manuallyStoppedRef.current) autoScroll?.play();
    };

    emblaApi.on("pointerDown", onPointerDown);
    emblaApi.on("settle", onSettle);
    rootNode.addEventListener("mouseenter", onMouseEnter);
    rootNode.addEventListener("mouseleave", onMouseLeave);

    return () => {
      emblaApi.off("pointerDown", onPointerDown);
      emblaApi.off("settle", onSettle);
      rootNode.removeEventListener("mouseenter", onMouseEnter);
      rootNode.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const autoScroll = emblaApi.plugins().autoScroll;
    manuallyStoppedRef.current = stopped;
    if (stopped) autoScroll?.stop();
    else if (!isMouseOverRef.current) autoScroll?.play();
  }, [emblaApi, stopped]);

  useEffect(() => {
    if (!emblaApi) return;
    const autoScroll = emblaApi.plugins().autoScroll;
    (window as any).__stopGrid = () => {
      manuallyStoppedRef.current = true;
      autoScroll?.stop();
    };
    (window as any).__playGrid = () => {
      manuallyStoppedRef.current = false;
      autoScroll?.play();
    };
    return () => {
      delete (window as any).__stopGrid;
      delete (window as any).__playGrid;
    };
  }, [emblaApi]);

  if (isFeedLoading || items.length === 0) return null;

  return (
    <div className="max-lg:-mx-4 overflow-hidden">
      {/* Header row — title left, counter right */}
      <div className="max-lg:px-4 flex items-center justify-between mb-2 lg:mb-4">
        <p className="font-jost font-semibold text-base lg:text-lg leading-5 lg:leading-6 whitespace-nowrap">
          <span className="text-dark-1">Another winner. </span>
          <span className="text-[#ff8200]">Now.</span>
        </p>
        <div className="flex items-center gap-2">
          {dailyTotals && <RotatingStats totals={dailyTotals} variant="grid" />}
        </div>
      </div>

      {/* Continuous marquee with drag support */}
      <div ref={emblaRef} className="embla__viewport overflow-hidden touch-pan-y select-none">
        <div className="embla__container flex">
          {slides.map((item, i) => (
            <div
              key={i}
              className="embla__slide w-[191px] lg:w-[265px] shrink-0 [contain:layout_style_paint] mr-2 lg:mr-4">
              <FeedCardVertical item={item} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
